import React, { useEffect, useState } from "react";
import "./AddPartners.css";
import { Flex, SafeArea } from "../../components/layout";
import { Button, FileInput, Form, Input, Title } from "../../components/ui";
import Editor from "../../components/common/editor/Editor";
import { db, storage } from "../../components/firebase/firebase";
import {
  setDoc,
  doc,
  query,
  getDocs,
  collection,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { slugify } from "../../components/function";
import Notification from "../../components/ui/alert/Notification";
import ComboBox from "../../components/ui/input/ComboBox";
import { optimizeImage, replaceImageUrls } from "../../functions/image";
import { useNavigate, useParams } from "react-router-dom";
import { logEvent } from "../../utils/logger";

function AddPartners() {
  const [title, setTitle] = useState("");
  const [activity, setActivity] = useState("");
  const [imageFiles, setImageFiles] = useState([]); // Stocke les images pour l'upload
  const [selected, setSelected] = useState([]); // Stocke les ateliers sélectionnés
  const [workshops, setWorkshops] = useState([]); // Stocke les ateliers sélectionnés
  const [image, setImage] = useState([]); // Stocke les images pour l'upload
  const [loading, setLoading] = useState(false);
  const [editorContent, setEditorContent] = useState();
  const [notification, setNotification] = useState(null);

  const fetchWorkshops = async () => {
    try {
      const workshops = await getDocs(query(collection(db, "workshops")));
      const data = workshops.docs.map((doc) => doc.data().title);
      setWorkshops(data);
    } catch (error) {
      logEvent("ERROR", "DB", "Failed to fetch workshops for partner form", {
        error: error.message,
        stack: error.stack,
      });
      console.error("Erreur lors du chargement des articles :", error);
    }
  };
  const navigate = useNavigate();

  const { id } = useParams();

  const fetchPartner = async () => {
    const docRef = doc(db, "partners", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      setTitle(data.title);
      setEditorContent(data.content);
      setActivity(data.activity);
      setSelected(data.workshops.map((doc) => doc));
      setImage([{ preview: data.pictureprofile, file: null }]);
    } else {
      console.log("No such document!");
    }
  };
  useEffect(() => {
    fetchWorkshops();
    if (id) {
      fetchPartner();
    }
  }, []);

  // Upload des images vers Firebase Storage après optimisation
  const uploadImagesToStorage = async () => {
    const uploadedImages = [];

    for (const img of imageFiles) {
      try {
        const optimizedImage = await optimizeImage(img.file); // ✅ Compression si nécessaire
        const fileExtension = image[0]?.file?.name.split(".").pop(); // Récupère l'extension du fichier
        const storageRef = ref(
          storage,
          `partners/${slugify(title)}.${fileExtension}`,
        );

        await uploadBytes(storageRef, optimizedImage);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedImages.push({ oldUrl: img.url, newUrl: downloadURL });
      } catch (error) {
        logEvent("ERROR", "DB", "Partner content image upload failed", {
          error: error.message,
          stack: error.stack,
          partnerTitle: title,
        });
        console.error("Erreur lors de l'upload :", error);
      }
    }

    return uploadedImages;
  };

  const uploadImageToStorage = async () => {
    try {
      const optimizedImage = await optimizeImage(image[0].file); // ✅ Compression si nécessaire
      const storageRef = ref(
        storage,
        `partners/${slugify(title)}.${
          image[0]?.file?.type?.split("/")[1] || ""
        }`,
      );
      await uploadBytes(storageRef, optimizedImage);
      return await getDownloadURL(storageRef);
    } catch (error) {
      logEvent("ERROR", "DB", "Partner profile image upload failed", {
        error: error.message,
        stack: error.stack,
        partnerTitle: title,
      });
      console.error("Erreur lors de l'upload :", error);
    }
  };

  // Sauvegarde de l’article dans Firestore avec images finales
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!title || !activity || !editorContent) {
      setNotification({
        type: "error",
        message: "Veuillez remplir tous les champs.",
      });
      setLoading(false);
      return;
    }

    try {
      const images = await uploadImagesToStorage();
      let imageUrl = null;
      if (image[0].file) {
        imageUrl = await uploadImageToStorage();
      } else {
        imageUrl = image[0].preview;
      }
      const finalContent = replaceImageUrls(editorContent, images);

      if (id && id !== slugify(title)) {
        await deleteDoc(doc(db, "partners", id));
      }
      await setDoc(doc(db, "partners", slugify(title)), {
        pictureprofile: imageUrl,
        title,
        activity,
        content: finalContent,
        workshops: selected,
        createdAt: Date.now(),
      });

      setTitle("");
      setActivity("");
      setImage([]);
      setSelected([]);
      setImageFiles([]);
      setEditorContent("");
      setNotification({
        type: "success",
        message: "Action réalisée avec succès.",
      });
      logEvent("INFO", "DB", id ? "Partner updated" : "Partner created", {
        slug: slugify(title),
        title,
        activity,
        workshopsLinked: selected.length,
        editedFrom: id || null,
      });
      if (id) {
        navigate("/partners/manage");
      }
    } catch (error) {
      logEvent("ERROR", "DB", "Partner save failed", {
        error: error.message,
        stack: error.stack,
        title,
        isEdit: !!id,
      });
      console.error("Erreur lors de la sauvegarde :", error);
      setNotification({ type: "error", message: "Échec de la sauvegarde." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeArea className="AddPartners">
      <Notification
        state={notification}
        message={notification?.message || ""}
        type={notification?.type || "success"}
        onClose={() => setNotification(null)}
      />
      <Flex type="col" gap={50}>
        <Title level="h3">{id ? "Modifier" : "Ajouter"} un partenaire</Title>
        <Form>
          <Flex type="col" gap={20}>
            <FileInput
              label="Photo de profile"
              multiple={false}
              onChange={(e) =>
                setImage([
                  {
                    preview: URL.createObjectURL(e.target.files[0]),
                    file: e.target.files[0],
                  },
                ])
              }
              value={image}
              accept=".jpg,.png,.webp"
            />
            <Input
              label="Prénom et nom"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Input
              label="Activité"
              type="text"
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
            />
            <Editor
              label="Contenu"
              setImageFiles={setImageFiles}
              onChange={setEditorContent}
              value={editorContent}
            />
            <ComboBox
              label="Ajouter un/des ateliers"
              onChange={(e) => setSelected(e)}
              value={selected}
              options={workshops}
            />
            <Flex type="end-center" className="ctn-btn" gap={15}>
              <Button skin="nobg" to={id ? "/partners/manage" : "/"}>
                Annuler
              </Button>
              <Button onClick={handleSave} loading={loading}>
                Publier
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </SafeArea>
  );
}

export default AddPartners;
